import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import BootstrapTable from "../../../../share-components/table/BootstrapTable";
import PopoverFormula from './PopoverFormula';
import { PopoverBody } from 'reactstrap';
import { toast } from "react-toastify";

import {
    getFinancesPartnerXeroInvoices,
    republishPartnerXeroInvoice
} from "../store/financesSummarySlice";

import {
    formatterCurrency
} from "../../../../utils/common";

const PartnerXeroInvoice = (props) => {

    const {
        filters: mainFilters,
    } = props;

    const dispatch = useDispatch();

    const financesSummaryState = useSelector(
        ({ report }) => report.financesSummary
    );
    
    const [lastFillters, setLastFillters] = useState(null);
    const [filter, setFilter] = useState({
        ...mainFilters,
        page: 1,
        pageSize: 5,
        draw: 1,
    });

    const onPageSizeChange = (newPageSize) => {
        setFilter(prev => ({
            ...prev,
            page: 1,
            pageSize: newPageSize === 'All' ? financesSummaryState.partnerXeroInvoiceTableRow : newPageSize
        }));
    }

    const onPageChange = (e) => {
        setFilter({ ...filter, page: e });        
    }

    const [loading, setLoading] = useState(true);
    const [timer, setTimer] = useState(null);

    // Track IDs that are currently being republished (to disable button while API call is in flight)
    const [inProgressIds, setInProgressIds] = useState([]);
    // Track IDs that have been republished (so button remains disabled / shows Done immediately)
    const [republishedIds, setRepublishedIds] = useState([]);

    const addInProgress = (id) => setInProgressIds(prev => (prev.includes(id) ? prev : [...prev, id]));
    const removeInProgress = (id) => setInProgressIds(prev => prev.filter(i => i !== id));
    const addRepublished = (id) => setRepublishedIds(prev => (prev.includes(id) ? prev : [...prev, id]));

    const getFinancesPaymentsData = () => {
        if (inProgressIds.length > 0) return; // skip refresh if any republish is in progress

        if (JSON.stringify(filter) == JSON.stringify(lastFillters)) return;

        setInProgressIds([]); // clear in-progress IDs when filters change
        setRepublishedIds([]); // clear republished IDs when filters change

        setLastFillters({ ...filter });
        setLoading(true);

        dispatch(
            getFinancesPartnerXeroInvoices(filter)
        ).then((res) => {
            if (!res.error) {
                setLoading(false);
            }
        });
    };

    useEffect(() => {
        setFilter({
            ...filter,
            ...mainFilters,
            page: 1,
        });
    }, [mainFilters])

    useEffect(() => {
        clearTimeout(timer);
        const newTimer = setTimeout(() => {
            getFinancesPaymentsData();
        }, 100);
        setTimer(newTimer)
    }, [dispatch, filter])

    const handleRepublish = (row) => {
        const data = { partner_invoice_id: row.id };
        // mark as in-progress immediately to disable the button
        addInProgress(row.id);

        dispatch(republishPartnerXeroInvoice(data)).then((res) => {
            // always clear in-progress flag when API responds
            removeInProgress(row.id);

            if (!res.error) {
                // mark as republished so UI shows Done and stays disabled
                addRepublished(row.id);
                toast.success("Invoice re-published successfully");
                // Refresh the list after successful republish
                setFilter(prev => ({ ...prev, draw: prev.draw + 1 }));
            } else {
                toast.error(res.payload?.message || "Failed to re-publish");
            }
        }).catch(() => {
            // network/other error
            removeInProgress(row.id);
            toast.error("Failed to re-publish");
        });
    };

    const tableColumns = [
        {
            name: 'Date',
            selector: row => row.invoice_date,
            left: true,
            grow: 1, // column size
        },
        {
            name: 'Invoice #',
            selector: row => row.invoice_number,
            left: true,
            grow: 0.5, // Larger column
        },
        {
            name: 'Contact',
            selector: row => row.partner_name,
            left: true,
            wrap: true, // Allow text wrapping
        },
        {
            name: 'Xero Status',
            selector: row => row.xero_status,
            left: true,
            grow: 0.5, // column size
        },
        {
            name: 'Currency',
            selector: row => row.currency,
            left: true,
            grow: 0.5, // column size
        },
        {
            name: 'Amount',
            selector: row => {
                return (
                    <>
                        {row.error_message && row.error_message != '' && (
                        <PopoverFormula className="mr-2" id={row.id + '_amountNotMatch'}>
                            <PopoverBody>
                                <p>{row.error_message}</p>
                            </PopoverBody>
                        </PopoverFormula>
                        )}

                        {formatterCurrency(row.total_gross_amount, row.currency)}
                        {row.total_gross_amount !== row.xero_total_gross_amount && (
                        <small className="text-danger d-block">{formatterCurrency(row.xero_total_gross_amount, row.currency)}</small>
                        )}
                    </>
                ); 
            },
            right: true,
            grow: 0.5, // column size
        },
        {
            name: 'Actions',
            selector: row => {
                const isInProgress = inProgressIds.includes(row.id);
                const isRepublished = republishedIds.includes(row.id) || !row.is_republish;

                return (
                    <button
                        type="button"
                        className={`btn btn-xs ${isRepublished ? 'btn-success' : 'btn-primary'}`}
                        disabled={isInProgress || isRepublished}
                        onClick={() => handleRepublish(row)}
                        title={isRepublished ? 'Re-publish not available' : (isInProgress ? 'Re-publishing...' : 'Re-publish')}
                    >
                        {isInProgress ? <i className="fa fa-spinner fa-spin" aria-hidden="true" /> :
                            (isRepublished ? <span>Done</span> : <i className="fa fa-upload" aria-hidden="true" />)
                        }
                    </button>
                );
            },
            right: true,
            grow: 0.4,
        },
    ];

    return (
        <div className="card">
            {loading && (
                <div className="card-loading">
                    <i className="fa fa-spinner fa-lg spinner" />
                </div>
            )}

            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">Partner Xero Invoices</h5>
                    <a href="/report/xero-invoices" className="btn btn-primary btn-sm" title="View Invoices">
                        View Invoices
                    </a>
                </div>
                

                {financesSummaryState.partnerXeroInvoiceLists.length > 0 ? <>                
                    <BootstrapTable
                        headerColumns={tableColumns}
                        gridData={financesSummaryState.partnerXeroInvoiceLists}
                        noAction
                        progressPending={loading}
                        paginationServer
                        paginationTotalRows={financesSummaryState.partnerXeroInvoiceTableRow}
                        paginationRowsPerPageOptionsEnabled={false}
                        currentPage={filter.page}
                        perPage={filter.pageSize}
                        onChangePage={onPageChange}
                        onChangeRowsPerPage={onPageSizeChange}
                        size="md"
                    />
                </> : <>
                    <p className="text-muted font-italic">No payments data.</p>
                </>}

            </div>
        </div>
    );
};

export default PartnerXeroInvoice;
